import type { ClinicalNote } from '../../clients/data/types';
import { isAppointmentPostSession } from '../../clients/utils/appointmentPostSession';
import type { Appointment } from '../../clients/data/types';
import { isSessionDocumentationRecordType, normalizeMedicalRecordType } from '../../clients/data/medicalRecordTypes';

export type DocumentationStatus =
  | 'Pending Documentation'
  | 'Pending Signature'
  | 'Pending Cosignature'
  | 'Signed';

/**
 * Signature workflow state of a note that exists. `Pending Documentation` is only for a missing
 * note on a post-session appointment — see `deriveAppointmentDocumentationStatus`.
 */
export function deriveNoteDocumentationStatus(
  note: ClinicalNote | undefined,
): Exclude<DocumentationStatus, 'Pending Documentation'> {
  if (note?.noteStatus === 'Pending co-signature') {
    return 'Pending Cosignature';
  }
  if (note?.noteStatus === 'Signed') {
    return 'Signed';
  }
  return 'Pending Signature';
}

/** Documentation / signature workflow for a chart appointment (not FHIR `Appointment.status`). */
export function deriveAppointmentDocumentationStatus(
  apt: Pick<Appointment, 'date' | 'time' | 'hasNote' | 'endIso' | 'minutesDuration'>,
  linkedNote: ClinicalNote | undefined,
): DocumentationStatus | null {
  if (!apt.hasNote) {
    if (isAppointmentPostSession(apt)) {
      return 'Pending Documentation';
    }
    return null;
  }
  return deriveNoteDocumentationStatus(linkedNote);
}

/**
 * Documentation Status filters session documentation only. Assessments, chart documents, and other
 * non-session types stay visible so the filter does not empty the rest of the chart.
 */
export function noteMatchesDocumentationStatusFilter(
  note: ClinicalNote,
  noteStatusFilter: string[],
): boolean {
  if (noteStatusFilter.length === 0) return true;
  const recordType = normalizeMedicalRecordType(note.noteType) ?? note.noteType;
  if (!isSessionDocumentationRecordType(recordType)) return true;
  return noteStatusFilter.includes(deriveNoteDocumentationStatus(note));
}
